const TRACK_ORDER_API_URL = 'https://api.chukar.com/order-traking/track-order.php';

/**
 * Track Order Lookup
 *
 * Expected API response shape:
 * {
 *   "success": true,
 *   "order": {
 *     "name": "#12345",
 *     "fulfillments": [
 *       {
 *         "ship_date": "2026-06-02T10:00:00Z",
 *         "tracking_company": "FedEx",
 *         "tracking_number": "1234567890",
 *         "tracking_url": "https://..."
 *       }
 *     ]
 *   }
 * }
 */
class TrackOrderLookup extends HTMLElement {
  constructor() {
    super();
    this.form = this.querySelector('[data-track-order-form]');
    this.resultsPanel = this.querySelector('[data-track-order-results]');
    this.placeholder = this.querySelector('[data-track-order-placeholder]');
    this.messageEl = this.querySelector('[data-track-order-message]');
    this.loadingEl = this.querySelector('[data-track-order-loading]');
    this.submitBtn = this.querySelector('[data-track-order-submit]');

    this.apiUrl = this.dataset.apiUrl || TRACK_ORDER_API_URL;
    this.requireLastName = this.dataset.requireLastName === 'true';
    this.errorMessage = this.dataset.errorMessage;
    this.notFoundMessage = this.dataset.notFoundMessage;
    this.noTrackingMessage = this.dataset.noTrackingMessage;
    this.labels = {
      orderNumber: this.dataset.labelOrderNumber || 'Order Number',
      shipDate: this.dataset.labelShipDate || 'Ship Date',
    };
    this.trackingLinkPrefix = this.dataset.trackingLinkPrefix || 'Track Shipment';

    if (this.form) {
      this.form.addEventListener('submit', this.onSubmit.bind(this));
    }
  }

  showMessage(text, type = 'error') {
    if (!this.messageEl) return;
    this.messageEl.textContent = text;
    this.messageEl.className = 'track-order-lookup__message field-error-message track-order-lookup__message--visible';
  }

  hideMessage() {
    if (!this.messageEl) return;
    this.messageEl.className = 'track-order-lookup__message';
    this.messageEl.textContent = '';
  }

  setLoading(isLoading) {
    if (this.loadingEl) {
      this.loadingEl.classList.toggle('track-order-lookup__loading--visible', isLoading);
    }
    if (this.submitBtn) {
      this.submitBtn.disabled = isLoading;
    }
  }

  async onSubmit(event) {
    event.preventDefault();
    this.hideMessage();

    if (!this.apiUrl) {
      this.showMessage('Tracking lookup is not configured. Please contact customer service.');
      return;
    }

    const orderNumber = this.form.querySelector('[name="order_number"]')?.value?.trim();
    const lastName = this.form.querySelector('[name="last_name"]')?.value?.trim();

    if (!orderNumber) {
      this.showMessage('Please enter your order number and billing last name.');
      return;
    }

    if (this.requireLastName && !lastName) {
      this.showMessage('Please enter the billing last name for this order.');
      return;
    }

    this.setLoading(true);

    try {
      const response = await fetch(this.apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          order_number: orderNumber,
          last_name: lastName || undefined,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || !data.success) {
        this.showMessage(data.message || this.notFoundMessage || 'We could not find a matching order.');
        this.hideResults();
        return;
      }

      this.renderResults(data.order);
    } catch (error) {
      this.showMessage(this.errorMessage || 'Something went wrong. Please try again later.');
      this.hideResults();
    } finally {
      this.setLoading(false);
    }
  }

  hideResults() {
    if (this.resultsPanel) {
      this.resultsPanel.classList.remove('track-order-results--visible');
    }
    if (this.placeholder) {
      this.placeholder.style.display = '';
    }
  }

  renderResults(order) {
    if (!order || !this.resultsPanel) return;

    if (this.placeholder) {
      this.placeholder.style.display = 'none';
    }

    this.resultsPanel.classList.add('track-order-results--visible');

    const content = this.resultsPanel.querySelector('[data-result-content]');
    if (content) {
      this.renderOrderContent(content, order);
    }

    this.resultsPanel.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  renderOrderContent(container, order) {
    container.innerHTML = '';

    const fulfillments = order.fulfillments || [];
    const details = document.createElement('dl');
    details.className = 'track-order-details';
    details.innerHTML = this.renderDetailRow(this.labels.orderNumber, order.name || '—');
    container.appendChild(details);

    if (!fulfillments.length) {
      const message = document.createElement('p');
      message.className = 'contact-text track-order-shipments__message';
      message.textContent =
        this.noTrackingMessage || 'Your order has not shipped yet. Tracking will appear here once available.';
      container.appendChild(message);
      return;
    }

    fulfillments.forEach((fulfillment) => {
      const block = document.createElement('div');
      block.className = 'track-order-shipment';

      const shipDate = fulfillment.created_at || fulfillment.ship_date;
      const carrier = fulfillment.tracking_company || 'Carrier';
      const trackingNumber = fulfillment.tracking_number;
      const trackingUrl = trackingNumber
        ? this.buildTrackingUrl(carrier, trackingNumber, fulfillment.tracking_url)
        : null;

      const shipDetails = document.createElement('dl');
      shipDetails.className = 'track-order-details track-order-shipment__details';
      shipDetails.innerHTML = this.renderDetailRow(
        this.labels.shipDate,
        shipDate ? this.formatDate(shipDate) : '—'
      );
      block.appendChild(shipDetails);

      const actions = document.createElement('div');
      actions.className = 'track-order-shipment__actions';

      if (trackingUrl) {
        const label = this.trackingLinkPrefix;
        const button = this.createTrackingButton(trackingUrl, label);
        if (button) {
          actions.appendChild(button);
        }
      } else {
        actions.innerHTML = `
          <p class="contact-text track-order-shipments__message">${this.escapeHtml(
            this.noTrackingMessage || 'Tracking is not yet available for this shipment.'
          )}</p>
        `;
      }

      block.appendChild(actions);
      container.appendChild(block);
    });
  }

  renderDetailRow(label, value) {
    return `
      <div class="track-order-details__row">
        <dt class="track-order-details__label">${this.escapeHtml(label)}</dt>
        <dd class="track-order-details__value">${this.escapeHtml(value)}</dd>
      </div>
    `;
  }

  formatDate(dateString) {
    if (!dateString) return '—';

    try {
      // Fulfillment created_at is in store time (e.g. -07:00). Format in Pacific
      // so customers in other timezones don't see the wrong calendar day.
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        timeZone: 'America/Los_Angeles',
      });
    } catch {
      return dateString;
    }
  }

  buildTrackingUrl(carrier, trackingNumber, trackingUrl) {
    if (!trackingNumber) return null;

    const cleanNumber = String(trackingNumber).replace(/[^0-9A-Za-z]/g, '');
    if (!cleanNumber) return null;

    const carrierLower = String(carrier || '').toLowerCase();
    if (carrierLower.includes('fedex')) {
      return `https://www.fedex.com/wtrk/track/?trknbr=${encodeURIComponent(cleanNumber)}`;
    }

    return trackingUrl || null;
  }

  createTrackingButton(trackingUrl, label) {
    const template = this.querySelector('[data-track-order-button-template]');
    if (!template?.content) return null;

    const fragment = template.content.cloneNode(true);
    const link = fragment.querySelector('a');

    if (!link) return null;

    link.href = trackingUrl;
    link.setAttribute('aria-label', label);

    const labelEl = fragment.querySelector('[data-track-order-link-label]');
    if (labelEl) {
      labelEl.textContent = label;
    }

    return fragment;
  }

  escapeHtml(str) {
    if (!str) return '';
    const div = document.createElement('div');
    div.textContent = String(str);
    return div.innerHTML;
  }
}

if (!customElements.get('track-order-lookup')) {
  customElements.define('track-order-lookup', TrackOrderLookup);
}

function initTrackOrderFaqToggles() {
  const faqQuestions = document.querySelectorAll('.track-order-support [data-faq-toggle]');

  faqQuestions.forEach((question) => {
    if (question.dataset.faqBound) return;
    question.dataset.faqBound = 'true';

    question.addEventListener('click', function () {
      const answer = this.nextElementSibling;
      const icon = this.querySelector('.faq__icon');
      const isOpen = answer.classList.contains('faq__answer--open');

      faqQuestions.forEach((otherQuestion) => {
        if (otherQuestion === this) return;
        const otherAnswer = otherQuestion.nextElementSibling;
        const otherIcon = otherQuestion.querySelector('.faq__icon');
        otherAnswer.classList.remove('faq__answer--open');
        otherQuestion.setAttribute('aria-expanded', 'false');
        otherIcon?.classList.remove('faq__icon--active');
      });

      if (isOpen) {
        answer.classList.remove('faq__answer--open');
        this.setAttribute('aria-expanded', 'false');
        icon?.classList.remove('faq__icon--active');
      } else {
        answer.classList.add('faq__answer--open');
        this.setAttribute('aria-expanded', 'true');
        icon?.classList.add('faq__icon--active');
      }
    });
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initTrackOrderFaqToggles);
} else {
  initTrackOrderFaqToggles();
}
